import { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Switch, message, Space, Typography, Alert, Spin } from 'antd';
import { CloudOutlined, SaveOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

const { Title, Text } = Typography;

const NextCloudSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/api/nextcloud/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      form.setFieldsValue({
        url: res.data.settings.url,
        username: res.data.settings.username === '***' ? '' : res.data.settings.username,
        storageType: res.data.settings.storageType === 'nextcloud'
      });
    } catch (error) {
      message.error('ไม่สามารถดึงการตั้งค่าได้');
    } finally {
      setSettingsLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      setTesting(true);
      setConnectionStatus(null);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/api/nextcloud/test-connection`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectionStatus(res.data.success ? 'success' : 'error');
      message[res.data.success ? 'success' : 'error'](res.data.message);
    } catch (error) {
      setConnectionStatus('error');
      message.error('ไม่สามารถทดสอบการเชื่อมต่อได้');
    } finally {
      setTesting(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/nextcloud/settings`, {
        url: values.url,
        username: values.username,
        password: values.password,
        storageType: values.storageType ? 'nextcloud' : 'local'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('บันทึกการตั้งค่าเรียบร้อยแล้ว');
      // Reload settings to update display
      fetchSettings();
    } catch (error) {
      message.error('ไม่สามารถบันทึกการตั้งค่าได้');
    } finally {
      setLoading(false);
    }
  };

  if (settingsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <CloudOutlined /> ตั้งค่า NextCloud
      </Title>
      <Text type="secondary">
        ตั้งค่าการเชื่อมต่อ NextCloud สำหรับจัดเก็บไฟล์
      </Text>

      <Card style={{ marginTop: '24px' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Form.Item
            name="url"
            label="NextCloud URL"
            rules={[{ required: true, message: 'กรุณากรอก NextCloud URL' }]}
            tooltip="URL ของ NextCloud WebDAV เช่น https://cloud.example.com/remote.php/dav/files/username"
          >
            <Input 
              placeholder="https://cloud.atom-soft.com/remote.php/dav/files/sangha45"
              prefix={<CloudOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="username"
            label="ชื่อผู้ใช้"
            rules={[{ required: true, message: 'กรุณากรอกชื่อผู้ใช้' }]}
          >
            <Input placeholder="ชื่อผู้ใช้ NextCloud" />
          </Form.Item>

          <Form.Item
            name="password"
            label="รหัสผ่าน"
            rules={[{ required: true, message: 'กรุณากรอกรหัสผ่าน' }]}
            tooltip="แนะนำให้ใช้ App Password แทนรหัสผ่านหลัก"
          >
            <Input.Password placeholder="รหัสผ่านหรือ App Password" />
          </Form.Item>

          <Form.Item
            name="storageType"
            label="ใช้ NextCloud สำหรับจัดเก็บไฟล์"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SaveOutlined />}
                loading={loading}
              >
                บันทึกการตั้งค่า
              </Button>
              <Button 
                onClick={testConnection}
                loading={testing}
                icon={<CheckCircleOutlined />}
              >
                ทดสอบการเชื่อมต่อ
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {connectionStatus === 'success' && (
          <Alert
            message="เชื่อมต่อสำเร็จ"
            description="สามารถเชื่อมต่อกับ NextCloud ได้แล้ว"
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginTop: '16px' }}
          />
        )}

        {connectionStatus === 'error' && (
          <Alert
            message="เชื่อมต่อไม่สำเร็จ"
            description="ไม่สามารถเชื่อมต่อกับ NextCloud ได้ กรุณาตรวจสอบการตั้งค่า"
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
            style={{ marginTop: '16px' }}
          />
        )}
      </Card>

      <Card style={{ marginTop: '24px' }} title="ข้อมูลเพิ่มเติม">
        <Space direction="vertical">
          <Text strong>วิธีการใช้งาน:</Text>
          <ul>
            <li>กรอก NextCloud URL, Username และ Password</li>
            <li>กดปุ่ม "ทดสอบการเชื่อมต่อ" เพื่อตรวจสอบ</li>
            <li>เปิดสวิตช์ "ใช้ NextCloud สำหรับจัดเก็บไฟล์" เพื่อเปิดใช้งาน</li>
            <li>กดปุ่ม "บันทึกการตั้งค่า" เพื่อบันทึก</li>
          </ul>
          <Text strong>คำแนะนำ:</Text>
          <ul>
            <li>ใช้ App Password ของ NextCloud แทนรหัสผ่านหลักเพื่อความปลอดภัย</li>
            <li>สามารถสร้าง App Password ได้ที่ Settings {'>'} Security {'>'} Devices & sessions</li>
            <li>หากปิดใช้งาน NextCloud ไฟล์จะถูกจัดเก็บใน Local Storage แทน</li>
          </ul>
        </Space>
      </Card>
    </div>
  );
};

export default NextCloudSettings;
